import type { EntryPoint, EntryPointComponent } from 'react-relay';

import { RovoAgentAri } from '@atlassian/ari';
import { JSResourceForInteraction } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';

import agentProfileCardQueryParameters from './__generated__/AgentProfileCardContainerQuery.graphql.ts';
import type { AgentProfileCardQueries } from './AgentProfileCardContainer.tsx';
import type { AgentProfileCardContainerExtraProps } from './AgentProfileCardContainer.tsx';
import type { AgentProfileCardRuntimeProps } from './types.ts';

export type AgentProfileCardEntryPointParams =
	| { agentAri: string }
	| { agentId: string; activationId: string }
	| { identityAccountId: string; cloudId: string };

type ResolvedQueryVariables = {
	agentAri: string;
	identityAccountId: string;
	cloudId: string;
	fetchByIdentity: boolean;
};

function resolveQueryVariables(params: AgentProfileCardEntryPointParams): ResolvedQueryVariables {
	if ('identityAccountId' in params) {
		return {
			agentAri: '',
			identityAccountId: params.identityAccountId,
			cloudId: params.cloudId,
			fetchByIdentity: true,
		};
	}
	const agentAri =
		'agentAri' in params
			? params.agentAri
			: RovoAgentAri.create({
					activationId: params.activationId,
					agentId: params.agentId,
				}).toString();
	return { agentAri, identityAccountId: '', cloudId: '', fetchByIdentity: false };
}

export const agentProfileCardEntryPoint: EntryPoint<
	EntryPointComponent<
		AgentProfileCardQueries,
		Record<string, never>,
		AgentProfileCardRuntimeProps,
		AgentProfileCardContainerExtraProps
	>,
	AgentProfileCardEntryPointParams
> = createEntryPoint({
	root: JSResourceForInteraction(
		() =>
			import(
				/* webpackChunkName: "@atlassian-internal_conversation-assistant-agent_agent-profile-card" */ './AgentProfileCardContainer.tsx'
			),
		{
			moduleId: 'conversation-assistant-agent_agent-profile-card',
		},
	),
	getPreloadProps: (params: AgentProfileCardEntryPointParams) => {
		const queryVariables = resolveQueryVariables(params);

		return {
			queries: {
				agentProfileCardQuery: {
					parameters: agentProfileCardQueryParameters,
					variables: queryVariables,
					options: { fetchPolicy: 'store-or-network' as const },
				},
			},
		};
	},
});
